import React, { CSSProperties, Suspense } from 'react'
import {
  AlertCircle,
  Compass,
  HelpCircle,
  Layout,
  RefreshCcw,
  Share2,
} from 'react-feather'
import { LuShieldCheck } from 'react-icons/lu'
import { useLocation } from 'react-router'
import { useMount } from 'react-use'

import AppHeader from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AppHeader'
import DashboardSidebar from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebar'
import DashboardSidebarProvider from '@/components/layouts/DashboardLayout/components/DashboardSidebar/DashboardSidebarProvider'
import Footer from '@/components/layouts/DashboardLayout/components/Footer'
import ReviewPartnerModal from '@/components/layouts/DashboardLayout/components/ReviewPartnerModal'
import PageContent from '@/components/misc/PageContent'
import SentryErrorBoundary from '@/components/misc/SentryErrorBoundary'
import { SidebarInset } from '@/components/ui/sidebar'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import useAppDispatch from '@/hooks/useAppDispatch'
import { useAppStateQuery } from '@/hooks/useAppStateQuery'
import { useClientMetadata } from '@/hooks/useClientMetadata'
import { useLatestCompassReportQuery } from '@/hooks/useLatestCompassReportQuery'
import {
  CLIENT_DASHBOARD_SIDEBAR_LINKS,
  COMPASS_REPORT_STATUS,
} from '@/lib/constants'
import { extractIdentifierFromIri } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
import CompassMetadataProvider from '@/providers/CompassMetadataProvider'
import { LAYOUTS, setLayout } from '@/redux/slices/layout'
import { SidebarLink } from '@/types/misc'
import { ClientMetadataItem } from '@/types/responses/client-metadata'

interface Props {
  children: React.ReactNode
}

const ClientDashboardLayout: React.FC<Props> = ({ children }) => {
  const location = useLocation()
  const metadata = useClientMetadata() as ClientMetadataItem
  const dispatch = useAppDispatch()

  const latestCompassReportQuery = useLatestCompassReportQuery()

  useMount(() => {
    dispatch(setLayout(LAYOUTS.clientDashboard))
  })

  const appStateQuery = useAppStateQuery()

  if (
    latestCompassReportQuery.isLoading &&
    !latestCompassReportQuery.isFetched
  ) {
    return <LoadingWidget />
  }

  const latestCompassReport = latestCompassReportQuery.data

  function buildSidebarLinks(): SidebarLink[] {
    const fontAwesomeIconStyles: CSSProperties = {
      fontSize: '24px',
      position: 'relative',
    }

    const links: SidebarLink[] = [
      {
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.compassDashboard,
        href: ROUTES.compassDashboard(),
        icon: <Layout />,
      },
    ]

    if (metadata.hasCompassReport) {
      links.push({
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.yourSnapshots,
        href: ROUTES.compassSnapshots(),
        icon: <Compass />,
      })
    } else {
      links.push({
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.startCompass,
        href: ROUTES.compassStart(),
        icon: <Compass />,
      })
    }

    if (metadata.latestCompletedCompassReport) {
      const token = extractIdentifierFromIri(
        metadata.latestCompletedCompassReport,
      )

      links.push({
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.actions,
        href: ROUTES.compassActions({ params: { token } }),
        icon: <AlertCircle />,
      })
    }

    if (metadata.hasCompassReport && metadata.sponsorCompany?.hasPanel) {
      links.push({
        title:
          latestCompassReport?.status === COMPASS_REPORT_STATUS.COMPLETE
            ? CLIENT_DASHBOARD_SIDEBAR_LINKS.updateCompass
            : CLIENT_DASHBOARD_SIDEBAR_LINKS.continueCompass,
        href: ROUTES.compassUpdate(),
        icon: <RefreshCcw />,
      })
    }

    if (latestCompassReport?.status === COMPASS_REPORT_STATUS.COMPLETE) {
      links.push({
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.ourPartners,
        href: ROUTES.compassPartners(),
        icon: (
          <LuShieldCheck style={{ ...fontAwesomeIconStyles, top: '1px' }} />
        ),
      })
    }

    links.push(
      {
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.sharing,
        href: ROUTES.compassSharing(),
        icon: <Share2 />,
      },
      {
        title: CLIENT_DASHBOARD_SIDEBAR_LINKS.howItWorks,
        href: ROUTES.compassHowItWorks(),
        icon: <HelpCircle />,
      },
    )

    return links
  }

  const sidebarLinks = buildSidebarLinks()

  if (appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  return (
    <>
      <CompassMetadataProvider>
        <ReviewPartnerModal />

        <DashboardSidebarProvider>
          <DashboardSidebar links={sidebarLinks} />

          <SidebarInset className="min-w-0">
            <AppHeader />
            <PageContent>
              <SentryErrorBoundary key={location.pathname}>
                <Suspense fallback={<LoadingWidget />}>{children}</Suspense>
              </SentryErrorBoundary>
            </PageContent>
            <Footer />
          </SidebarInset>
        </DashboardSidebarProvider>
      </CompassMetadataProvider>
    </>
  )
}

export default ClientDashboardLayout
